/**
 * Bounded client upload pool for Song Uploader bulk drops.
 *
 * Staging ingress (authorizer + uploadsCreate) has reserved concurrency.
 * Firing every file's POST /uploads at once saturates those slots and yields
 * API Gateway 503 "Service Unavailable". Keep in-flight init/put work capped
 * so excess files wait locally until a worker slot frees.
 */

export const DEFAULT_UPLOAD_CONCURRENCY = 3;
export const DEFAULT_UPLOAD_MAX_ATTEMPTS = 5;

const TRANSIENT_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

export function extractHttpStatus(err: unknown): number | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  if (typeof anyErr.status === "number") return anyErr.status;
  const data = anyErr.data as Record<string, unknown> | undefined;
  if (data && typeof data.statusCode === "number") return data.statusCode;
  if (typeof anyErr.statusCode === "number") return anyErr.statusCode;
  return null;
}

/** True when the failure is worth automatic retry (throttling / brief outage). */
export function isTransientUploadError(err: unknown): boolean {
  const status = extractHttpStatus(err);
  if (status != null && TRANSIENT_STATUS.has(status)) return true;

  const message =
    err && typeof err === "object" && typeof (err as { message?: unknown }).message === "string"
      ? ((err as { message: string }).message || "").toLowerCase()
      : typeof err === "string"
        ? err.toLowerCase()
        : "";

  if (!message) return false;
  return (
    message.includes("service unavailable") ||
    message.includes("temporarily unavailable") ||
    message.includes("too many requests") ||
    message.includes("timeout") ||
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("load failed") ||
    message.includes("econnreset") ||
    message.includes("etimedout")
  );
}

/** Operator-facing label — never lead with raw infrastructure "Service Unavailable". */
export function formatUploadTransientMessage(attempt: number, maxAttempts: number): string {
  return `Upload temporarily unavailable — retrying (${attempt} of ${maxAttempts})…`;
}

export function formatUploadExhaustedMessage(maxAttempts: number): string {
  return `Upload could not be submitted after ${maxAttempts} attempts. Wait a moment, then try again.`;
}

export function uploadRetryDelayMs(attempt: number): number {
  // attempt is 1-based after a failure (next attempt number).
  const base = Math.min(8000, 400 * 2 ** Math.max(0, attempt - 1));
  const jitter = Math.floor(Math.random() * 250);
  return base + jitter;
}

/**
 * Run async tasks with a fixed worker pool. Order of start is FIFO; completion
 * order is not guaranteed. One rejection does not cancel siblings.
 */
export async function runPool<T>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  const limit = Math.max(1, Math.floor(concurrency) || 1);
  let next = 0;

  async function runWorker(): Promise<void> {
    while (next < items.length) {
      const index = next;
      next += 1;
      await worker(items[index], index);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () =>
    runWorker(),
  );
  await Promise.all(workers);
}

export function newUploadIdempotencyKey(fileName: string): string {
  const safe = fileName.replace(/[^\w.-]+/g, "_").slice(0, 80);
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `upl_${crypto.randomUUID()}_${safe}`;
  }
  return `upl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}_${safe}`;
}
